import { atom, type createStore, type PrimitiveAtom } from 'jotai'
import { durableStateAtom } from '@/events/atoms'
import { pageMetaOf } from './pageMeta'

/** The current document's page id, or null for an ordinary document. A string, so it is stable across commits. */
export const pageIdAtom = atom((get): string | null => {
  // Also read by renderers in previews and lab pages whose store has no document loaded.
  try {
    return pageMetaOf(get(durableStateAtom).meta)?.id ?? null
  } catch {
    return null
  }
})

type Store = ReturnType<typeof createStore>

const pageLocalResets: Array<(store: Store) => void> = []

/**
 * An atom that belongs to the open page (screen to scene-layer state: a gobo, a lifted flap, drops
 * in flight). Like `atom(initial)`, and the page host puts it back to `initial` when the page closes
 * (`resetPageLocalAtoms`), so reopening a page starts fresh and nothing stays behind.
 */
export function pageLocalAtom<T>(initial: T): PrimitiveAtom<T> {
  const a = atom(initial)
  pageLocalResets.push((store) => store.set(a, initial))
  return a
}

/** Every `pageLocalAtom` back to its initial value (the page host, on close). */
export function resetPageLocalAtoms(store: Store): void {
  for (const reset of pageLocalResets) reset(store)
}
